/** @jsxRuntime classic */
/** @jsx jsx */
import PropTypes from 'prop-types';
import { memo, forwardRef } from 'react';
import { jsx } from '@emotion/core';
import Axis from "./Axis";
import Points from "./Points";
import Labels from "./Labels";
import Grid from "./Grid";
import Bars from "./Bar";
import { randomString } from "../../../utils";
const BarChart = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  labelsVisible,
  labelsCharacterWidth,
  labelsCountY,
  data,
  viewBoxHeight,
  labelsOffsetY,
  viewBoxWidth,
  labelsOffsetX,
  labelsHeightX,
  ...props
}, ref) => {
  const dataSource = data.map((item, i) => ({
    ...item,
    x: i
  }));
  const UNIT_WIDTH = 1;
  const id = 'DGN-CUI-BarChart-' + randomString(6, {
    allowNumber: false,
    allowSymbol: false
  });
  const round = n => {
    return Math.round(n * 100) / 100;
  };
  const getMinX = () => {
    return dataSource.length > 0 ? dataSource[0].x : 0;
  };
  const getMaxX = () => {
    return dataSource.length > 0 ? dataSource[dataSource.length - 1].x : 0;
  };
  const getMinY = () => {
    return 0;
  };
  const getMaxY = () => {
    const yStep = labelsCountY > 0 ? labelsCountY : 1;
    const maxY = dataSource.length > 0 ? dataSource.reduce((max, point) => point.quantity > max ? point.quantity : max, dataSource[0].quantity) : 0;
    return maxY ? Math.ceil(maxY / yStep) * yStep : yStep;
  };
  const getSvgX = x => {
    const minX = getMinX();
    const maxX = getMaxX();
    const paddingLeft = getLabelsYWidth();
    return round((x - minX) / (maxX + 1 - minX) * (Math.abs(viewBoxWidth) - paddingLeft));
  };
  const getSvgY = y => {
    const paddingBottom = labelsVisible ? labelsHeightX + labelsOffsetX : 0;
    const height = Math.abs(viewBoxHeight) - paddingBottom;
    const maxY = getMaxY();
    return round(height - y / maxY * height);
  };
  const getLabelsYWidth = () => {
    const maxY = getMaxY();
    return labelsVisible ? maxY.toString().length * labelsCharacterWidth + labelsOffsetY : 0;
  };
  const minX = getMinX();
  const maxX = getMaxX();
  const maxY = getMaxY();
  const minY = getMinY();
  const commonProps = {
    getX: getSvgX.bind(),
    getY: getSvgY.bind(),
    maxX: maxX,
    maxY: maxY,
    minX: minX,
    minY: minY,
    round: round,
    labelsWidthY: getLabelsYWidth(),
    unitWidth: UNIT_WIDTH
  };
  return jsx("svg", {
    id: id,
    ref: ref,
    viewBox: `0 0 ${Math.abs(viewBoxWidth)} ${Math.abs(viewBoxHeight)}`,
    style: {
      display: 'block',
      overflow: 'visible',
      margin: `${getLabelsYWidth() * 2}`
    }
  }, jsx(Axis, {
    ...props,
    ...commonProps
  }), jsx(Grid, {
    ...props,
    ...commonProps
  }), jsx(Bars, {
    data: dataSource,
    viewBoxWidth: viewBoxWidth,
    ...props,
    ...commonProps
  }), jsx(Labels, {
    data: dataSource,
    viewBoxWidth: viewBoxWidth,
    ...props,
    ...commonProps
  }), jsx(Points, {
    data: dataSource,
    ...props,
    ...commonProps
  }));
}));
BarChart.defaultProps = {
  data: [],
  viewBoxHeight: 200,
  viewBoxWidth: 500,
  ...Bars.defaultProps,
  ...Axis.defaultProps,
  ...Grid.defaultProps,
  ...Points.defaultProps,
  ...Labels.defaultProps
};
BarChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    x: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quantity: PropTypes.number
  })).isRequired,
  /** drawn Ratio of viewBoxHeight entered by user, default is: 200px */
  viewBoxHeight: PropTypes.number,
  /** drawn Ratio of viewBoxWidth entered by user, default is: 500px */
  viewBoxWidth: PropTypes.number,
  /** change color of Bar in BarChart */
  barsColor: PropTypes.string,
  /** change ColumB grid display style ['full' , 'horizontal', 'none']*/
  GridLines: PropTypes.string,
  //** change style show of label (default, italic) */
  labelStyle: PropTypes.string,
  /** the axisVisible Hide or show the Axis axis */
  axisVisible: PropTypes.bool,
  /** the axisColor change color */
  axisColor: PropTypes.string,
  /** change color of grid Columb*/
  gridColor: PropTypes.string,
  /** show data information when hovering in the area containing the column */
  pointsIsHoverOnZone: PropTypes.bool,
  /** show or hiden point */
  pointsVisible: PropTypes.bool,
  /** change the inner color of the point button */
  pointsColor: PropTypes.string,
  /** change the border color of the point button  */
  pointsStrokeColor: PropTypes.string,
  /** the thickness of the inner core point */
  pointsStrokeWidth: PropTypes.number,
  /** change the radius of the point is created */
  pointsRadius: PropTypes.number,
  ...Bars.propTypes,
  ...Grid.propTypes,
  ...Axis.propTypes,
  ...Points.propTypes,
  ...Labels.propTypes
};
export default BarChart;